import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import Thumbnail from 'components/Thumbnails/Thumbnail';
import Rating from 'components/Rating/Rating';
import Verified from 'components/Badges/Verified/Verified2';
import Pro from 'components/Badges/Pro/Pro';
import LastSeen from 'components/Shared/LastSeen';

import { calculateUserRating } from 'helpers/HelperFunctions';

import styles from './ApplicantSummaryCard.module.scss';

const ApplicantSummaryCard = ({ user, actionButtons }) => {
  if (!user) {
    return (
      <div className={styles.emptyState}>
        <p>No user data available</p>
      </div>
    );
  }

  const rating = calculateUserRating(user);
  const displaySkills = user.skills?.slice(0, 3) || [];
  const additionalSkillsCount =
    user.skills?.length > 3 ? user.skills.length - 3 : 0;
  const displayRoles = user.top_roles?.slice(0, 3) || [];

  return (
    <div className={styles.profileSection}>
      {/* Top section with avatar, name, location and action buttons (desktop) */}
      <div className={styles.topSection}>
        <div className={styles.profileHeader}>
          <Link
            className={styles.avatarLink}
            to={user.links?.main_relative || `/${user.username}`}
            target="_blank"
          >
            <Thumbnail
              thumbnails={user.avatars}
              className={styles.avatar}
              circle
            />
          </Link>

          <div className={styles.profileInfo}>
            <div className={styles.nameRow}>
              <Link
                className={styles.name}
                to={user.links?.main_relative || `/${user.username}`}
                target="_blank"
              >
                {user.displayname}
              </Link>
              <Verified
                user={user}
                style={{
                  position: 'relative',
                  top: '-1px',
                }}
              />
              <Pro
                user={user}
                style={{
                  display: 'inline',
                  fontSize: '10px',
                  position: 'relative',
                  top: '-1px',
                }}
              />
              <div className={styles.lastSeenIndicator}>
                <LastSeen user={user} />
              </div>
            </div>

            <div className={styles.ratingRow}>
              <Rating rating={rating} style={{ display: 'inline-block' }} />
              {user.rating?.count > 0 && (
                <span className={styles.ratingValue}>{rating.toFixed(1)}</span>
              )}
            </div>

            <div className={styles.location}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>{user.location?.text || 'Location not specified'}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons - desktop only */}
        {actionButtons && (
          <div className={styles.actionButtonsContainerDesktop}>
            {actionButtons}
          </div>
        )}
      </div>

      {/* Full width skills and roles */}
      <div className={styles.skills}>
        {displaySkills.map((skill, index) => (
          <span key={index} className={styles.skillBadge}>
            {typeof skill === 'string' ? skill : skill.name}
          </span>
        ))}
        {additionalSkillsCount > 0 && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`skills-tooltip-${user.id}`}>
                {user.skills.slice(3).map((skill, index) => (
                  <div key={index}>
                    {typeof skill === 'string' ? skill : skill.name}
                  </div>
                ))}
              </Tooltip>
            }
          >
            <span className={`${styles.skillBadge} ${styles.moreSkills}`}>
              +{additionalSkillsCount} more
            </span>
          </OverlayTrigger>
        )}
      </div>

      <div className={styles.roles}>
        {displayRoles.map((role, index) => (
          <span key={index} className={styles.roleBadge}>
            {role}
          </span>
        ))}
      </div>

      {/* Action Buttons - mobile only (at bottom) */}
      {actionButtons && (
        <div className={styles.actionButtonsContainerMobile}>
          {actionButtons}
        </div>
      )}
    </div>
  );
};

ApplicantSummaryCard.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
    displayname: PropTypes.string,
    avatars: PropTypes.shape({
      images: PropTypes.object,
    }),
    top_roles: PropTypes.arrayOf(PropTypes.string),
    skills: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          experience: PropTypes.number,
        }),
      ])
    ),
    location: PropTypes.shape({
      text: PropTypes.string,
    }),
    rating: PropTypes.shape({
      count: PropTypes.number,
      average: PropTypes.number,
    }),
    links: PropTypes.shape({
      main_relative: PropTypes.string,
    }),
    last_active: PropTypes.string,
  }),
  actionButtons: PropTypes.node,
};

export default ApplicantSummaryCard;
